import { useGLTF, useTexture } from '@react-three/drei';
import { DecalGeometry } from 'three/examples/jsm/geometries/DecalGeometry';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

import { useRecoilValue } from 'recoil';
import { logoTransformState } from 'state/langState';
import { getSafeTransform } from 'utils/getText';

export default function BoxDecalLogo({ logo, product }) {
  const { scene, nodes } = useGLTF('/obj/box.glb');

  const texture = useTexture(logo);

  const transforms = useRecoilValue(logoTransformState);
  const transform = getSafeTransform(transforms, product);

  /*
    geometry 관리
  */

  const geometryRefs = useRef([]);

  const [geometries, setGeometries] = useState([]);

  /*
    target mesh — 최초 1회
  */

  const targetMesh = nodes.Object_4;

  /*
    reusable objects (GC 최소화)
  */

  const boxRef = useRef(new THREE.Box3());
  const centerRef = useRef(new THREE.Vector3());
  const sizeRef = useRef(new THREE.Vector3());

  const quaternionRef = useRef(new THREE.Quaternion());
  const rotationRef = useRef(new THREE.Euler());
  const decalSizeRef = useRef(new THREE.Vector3());

  /*
    TEXTURE — 최초 1회
  */

  useEffect(() => {
    if (!texture) return;

    texture.flipY = true;

    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;

    texture.anisotropy = 8;

    texture.needsUpdate = true;
  }, [texture]);

  /*
    faces — static (재생성 방지)
  */

  const faces = useMemo(() => {
    return [
      { normal: new THREE.Vector3(1, 0, 0), zRotation: 0 },
      { normal: new THREE.Vector3(-1, 0, 0), zRotation: 0 },
      { normal: new THREE.Vector3(0, 1, 0), zRotation: 0 },
      { normal: new THREE.Vector3(0, -1, 0), zRotation: Math.PI },
      { normal: new THREE.Vector3(0, 0, 1), zRotation: 0 },
      { normal: new THREE.Vector3(0, 0, -1), zRotation: Math.PI },
    ];
  }, []);

  /*
    DECAL 생성
  */

  useEffect(() => {
    if (!targetMesh) return;

    /*
      기존 geometry 제거
    */

    geometryRefs.current.forEach((g) => g.dispose());
    geometryRefs.current = [];

    const newGeometries = [];

    /*
      reusable refs
    */

    const box = boxRef.current;
    const center = centerRef.current;
    const size = sizeRef.current;

    const quaternion = quaternionRef.current;
    const rotation = rotationRef.current;
    const decalSize = decalSizeRef.current;

    /*
      bounding box 계산
    */

    targetMesh.updateWorldMatrix(true, true);

    box.setFromObject(targetMesh);

    box.getCenter(center);

    box.getSize(size);

    /*
      controller scale
    */

    const scale = (transform.scale ?? 1) * 0.6;

    decalSize.set(scale, scale, scale);

    faces.forEach(({ normal, zRotation }) => {
      /*
        offset 계산
      */

      const offset = new THREE.Vector3(
        normal.x * size.x * 0.5,
        normal.y * size.y * 0.5,
        normal.z * size.z * 0.5
      );

      const position = center.clone().add(offset);

      /*
        rotation
      */

      quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);

      rotation.setFromQuaternion(quaternion);

      rotation.z = zRotation;

      /*
        geometry 생성
      */

      const geometry = new DecalGeometry(
        targetMesh,
        position,
        rotation,
        decalSize
      );

      geometryRefs.current.push(geometry);

      newGeometries.push(geometry);
    });

    setGeometries(newGeometries);

    return () => {
      geometryRefs.current.forEach((g) => g.dispose());
      geometryRefs.current = [];
    };
  }, [targetMesh, transform.scale, faces]);

  /*
    RENDER
  */

  return (
    <>
      <primitive object={scene} scale={0.08} />

      {geometries.map((geo, i) => (
        <mesh key={i} geometry={geo}>
          <meshStandardMaterial
            map={texture}
            transparent
            depthWrite={false}
            polygonOffset
            polygonOffsetFactor={-4}
          />
        </mesh>
      ))}
    </>
  );
}

useGLTF.preload('/obj/box.glb');
